<template>
  <div id="admin-push-notifications">
    <info-box v-bind:description="description"></info-box>
    <header-title title="PUSH NOTIFICATIONS" />

    <div v-if="notification" class="notification-banner" :class="notificationType">
      {{notification}}
    </div>

    <div class="two-column-layout">
      <div class="left-column">
        <div class="form-container">
          <h2>Send Push Notification</h2>

          <div class="form-group">
            <label>Notification Title:</label>
            <input
              type="text"
              v-model="notificationTitle"
              placeholder="Enter notification title"
              class="input-field"
            />
          </div>

          <div class="form-group">
            <label>Notification Message:</label>
            <textarea
              v-model="notificationMessage"
              placeholder="Enter notification message"
              class="textarea-field"
              rows="4"
            ></textarea>
          </div>

          <div class="form-group">
            <label>Target Audience:</label>
            <select v-model="targetType" class="select-field">
              <option value="topic">Send to Topic</option>
              <option value="user">Send to Specific User</option>
              <option value="endpoint">Send to Specific Endpoint</option>
            </select>
          </div>

          <div v-if="targetType === 'topic'" class="form-group">
            <label>Notification Type:</label>
            <div class="radio-group">
              <label>
                <input type="radio" v-model="selectedNotificationType" value="live" /> Live
              </label>
              <label>
                <input type="radio" v-model="selectedNotificationType" value="update_all" /> All
              </label>
              <label>
                <input type="radio" v-model="selectedNotificationType" value="update_minigames" /> Minigames
              </label>
              <label>
                <input type="radio" v-model="selectedNotificationType" value="update_party" /> Party
              </label>
              <label>
                <input type="radio" v-model="selectedNotificationType" value="update_playtogether" /> Play Together
              </label>
              <label>
                <input type="radio" v-model="selectedNotificationType" value="update_solosquad" /> SoloSquad
              </label>
              <label>
                <input type="radio" v-model="selectedNotificationType" value="update_other" /> Other
              </label>
            </div>
          </div>

          <div class="form-group" v-if="targetType === 'user'">
            <label>User ID:</label>
            <input
              type="text"
              v-model="targetUserId"
              placeholder="Enter user ID"
              class="input-field"
            />
          </div>

          <div class="form-group" v-if="targetType === 'endpoint'">
            <label>Endpoint ARN:</label>
            <input
              type="text"
              v-model="targetEndpoint"
              placeholder="Enter SNS endpoint ARN"
              class="input-field"
            />
          </div>

          <div class="form-group">
            <label>Platform:</label>
            <div class="checkbox-group">
              <label>
                <input type="checkbox" v-model="platforms.ios" /> iOS
              </label>
              <label>
                <input type="checkbox" v-model="platforms.android" /> Android
              </label>
            </div>
          </div>

          <div class="form-group">
            <label>Options:</label>
            <div class="checkbox-group">
              <label>
                <input type="checkbox" v-model="includeTwitchDeeplink" /> Include Twitch Deeplink
              </label>
            </div>
          </div>

          <div class="form-group">
            <label>Additional Data (JSON):</label>
            <textarea
              v-model="additionalData"
              placeholder='{"key": "value"}'
              class="textarea-field"
              rows="3"
            ></textarea>
          </div>

          <div class="button-group">
            <button
              class="btn-enabled"
              @click="sendNotification"
              :disabled="sending || !isFormValid"
            >
              {{sending ? 'Sending...' : 'Send Notification'}}
            </button>
            <button
              class="btn-secondary"
              @click="resetForm"
            >
              Reset Form
            </button>
          </div>

          <div class="stats-container" v-if="lastSendResult">
            <h3>Last Send Result:</h3>
            <div class="stats-content">
              <div v-if="lastSendResult.success">
                <p><strong>Status:</strong> <span class="success-text">Success</span></p>
                <p v-if="lastSendResult.messageId"><strong>Message ID:</strong> {{lastSendResult.messageId}}</p>
                <p v-if="lastSendResult.recipientCount"><strong>Recipients:</strong> {{lastSendResult.recipientCount}}</p>
              </div>
              <div v-else>
                <p><strong>Status:</strong> <span class="error-text">Failed</span></p>
                <p v-if="lastSendResult.error"><strong>Error:</strong> {{lastSendResult.error}}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="right-column">
        <div class="history-container">
          <h2>Send History</h2>
          <div v-if="loadingHistory" class="history-loading">Loading...</div>
          <div v-else-if="history.length === 0" class="history-empty">No notifications sent yet.</div>
          <div v-else class="history-list">
            <div
              v-for="entry in history"
              :key="entry.id"
              class="history-entry"
              :class="{ 'history-failed': !entry.success }"
            >
              <div class="history-header">
                <span class="history-badge" :class="entry.success ? 'badge-success' : 'badge-error'">
                  {{ entry.success ? 'OK' : 'FAIL' }}
                </span>
                <span class="history-type-badge" v-if="entry.category || entry.type">
                  {{ formatTypeBadge(entry) }}
                </span>
                <span class="history-time">{{ formatTime(entry.createdAt) }}</span>
              </div>
              <div class="history-title">{{ entry.title }}</div>
              <div class="history-body">{{ entry.message }}</div>
              <div class="history-meta">
                <span>by {{ entry.sent_by }}</span>
                <span v-if="entry.recipient_count"> | {{ entry.recipient_count }} recipients</span>
                <span> | {{ entry.target_type }}</span>
              </div>
              <div v-if="!entry.success && entry.error_message" class="history-error">
                {{ entry.error_message }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import axios from 'axios'

export default {
  name: 'admin-push-notifications',
  components: {
  },
  async mounted () {
    this.fetchHistory()
  },
  data () {
    return {
      description: {
        title: 'Admin Push Notifications',
        text: 'Send push notifications to users'
      },
      notification: undefined,
      notificationType: 'info',
      notificationTitle: '',
      notificationMessage: '',
      targetType: 'topic',
      selectedNotificationType: 'live',
      targetUserId: '',
      targetEndpoint: '',
      platforms: {
        ios: true,
        android: true,
        web: false
      },
      includeTwitchDeeplink: true,
      additionalData: '',
      sending: false,
      lastSendResult: null,
      history: [],
      loadingHistory: false
    }
  },
  computed: Object.assign(mapState(['currentUser']), {
    isFormValid () {
      if (!this.notificationTitle || !this.notificationMessage) {
        return false
      }

      if (this.targetType === 'topic' && !this.selectedNotificationType) {
        return false
      }

      if (this.targetType === 'user' && !this.targetUserId) {
        return false
      }

      if (this.targetType === 'endpoint' && !this.targetEndpoint) {
        return false
      }

      if (!this.platforms.ios && !this.platforms.android && !this.platforms.web) {
        return false
      }

      return true
    }
  }),
  methods: {
    async fetchHistory () {
      this.loadingHistory = true
      try {
        const response = await axios.get(`${Vue.config.CURRENCY_SERVICE_BASE_URL}/admin/push-notifications/history`, {withCredentials: true})
        this.history = response.data.history || []
      } catch (error) {
        console.error('Error fetching notification history:', error)
      } finally {
        this.loadingHistory = false
      }
    },
    formatTime (dateStr) {
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
        d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    },
    formatTypeBadge (entry) {
      if (entry.category === 'live') return 'Live'
      if (entry.type) return entry.type.charAt(0).toUpperCase() + entry.type.slice(1)
      return entry.target_type
    },
    async sendNotification () {
      this.sending = true
      this.lastSendResult = null

      try {
        // Validate JSON if provided
        let parsedData = null
        if (this.additionalData.trim()) {
          try {
            parsedData = JSON.parse(this.additionalData)
          } catch (e) {
            this.notify('Invalid JSON in additional data field', 'error')
            this.sending = false
            return
          }
        }

        const payload = {
          title: this.notificationTitle,
          message: this.notificationMessage,
          targetType: this.targetType,
          platforms: this.platforms
        }

        if (this.targetType === 'topic') {
          if (this.selectedNotificationType === 'live') {
            payload.category = 'live'
            payload.type = 'all'
          } else {
            payload.category = 'update'
            payload.type = this.selectedNotificationType.replace('update_', '')
          }
        } else if (this.targetType === 'user') {
          payload.userId = this.targetUserId
        } else if (this.targetType === 'endpoint') {
          payload.endpointArn = this.targetEndpoint
        }

        // Build data object with optional Twitch deeplink
        let dataObject = parsedData || {}
        if (this.includeTwitchDeeplink) {
          dataObject.twitch_deeplink = 'twitch://stream/swiftor'
        }

        // Only add data to payload if there's something in it
        if (Object.keys(dataObject).length > 0) {
          payload.data = dataObject
        }

        const response = await axios.post(`${Vue.config.CURRENCY_SERVICE_BASE_URL}/admin/push-notification`, payload, {withCredentials: true})

        this.lastSendResult = {
          success: true,
          messageId: response.data.messageId,
          recipientCount: response.data.recipientCount
        }

        this.notify('Push notification sent successfully!', 'success')
        this.fetchHistory()
      } catch (error) {
        console.error('Error sending notification:', error)

        const errorMessage = (error.response && error.response.data && error.response.data.error && error.response.data.error.message) || error.message

        this.lastSendResult = {
          success: false,
          error: errorMessage
        }

        this.notify(
          errorMessage || 'Failed to send notification. Check console for details.',
          'error'
        )
        this.fetchHistory()
      } finally {
        this.sending = false
      }
    },
    resetForm () {
      this.notificationTitle = ''
      this.notificationMessage = ''
      this.targetType = 'topic'
      this.selectedNotificationType = 'live'
      this.targetUserId = ''
      this.targetEndpoint = ''
      this.platforms = {
        ios: true,
        android: true,
        web: false
      }
      this.includeTwitchDeeplink = false
      this.additionalData = ''
      this.lastSendResult = null
    },
    notify (text, type = 'info') {
      this.notification = text
      this.notificationType = type
      setTimeout(() => {
        this.notification = undefined
      }, 5000)
    }
  }
}
</script>

<style scoped>
.notification-banner {
  padding: 15px;
  margin: 0 20px 20px;
  border-radius: 5px;
  text-align: center;
  font-weight: bold;
}

.notification-banner.info {
  background-color: #5A3E85;
  color: white;
}

.notification-banner.success {
  background-color: #4CAF50;
  color: white;
}

.notification-banner.error {
  background-color: var(--sl-red);
  color: white;
}

.two-column-layout {
  display: flex;
  gap: 20px;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.left-column {
  flex: 0 0 55%;
  min-width: 0;
}

.right-column {
  flex: 0 0 calc(45% - 20px);
  min-width: 0;
}

.form-container {
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
}

.form-container h2 {
  color: white;
  margin-bottom: 20px;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: bold;
}

.input-field,
.textarea-field,
.select-field {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 2px solid #5A3E85;
  background-color: #1a1a1a;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
}

.input-field:focus,
.textarea-field:focus,
.select-field:focus {
  outline: none;
  border-color: #7e55bb;
}

.textarea-field {
  resize: vertical;
  font-family: inherit;
}

.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: normal;
  margin-bottom: 0;
}

.radio-group input[type="radio"] {
  width: auto;
  cursor: pointer;
}

.checkbox-group {
  display: flex;
  gap: 20px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: normal;
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-top: 30px;
}

.btn-enabled {
  flex: 1;
  background-color: #5A3E85;
  color: white;
  border: none;
  border-top: 2px rgb(126, 85, 187) solid;
  padding: 12px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s;
  box-shadow: 0px 3px 3px 3px rgba(0, 0, 0, 0.4);
}

.btn-enabled:hover:not(:disabled) {
  background-color: #7e55bb;
}

.btn-enabled:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  flex: 1;
  background-color: #3a3a3a;
  color: white;
  border: none;
  border-top: 2px #5a5a5a solid;
  padding: 12px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s;
  box-shadow: 0px 3px 3px 3px rgba(0, 0, 0, 0.4);
}

.btn-secondary:hover {
  background-color: #4a4a4a;
}

.stats-container {
  margin-top: 30px;
  padding: 20px;
  background-color: #1a1a1a;
  border-radius: 5px;
  border: 1px solid #5A3E85;
}

.stats-container h3 {
  color: white;
  margin-bottom: 15px;
}

.stats-content p {
  color: white;
  margin: 8px 0;
}

.success-text {
  color: #4CAF50;
  font-weight: bold;
}

.error-text {
  color: #ff5555;
  font-weight: bold;
}

/* History panel */
.history-container {
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
}

.history-container h2 {
  color: white;
  margin-bottom: 20px;
  text-align: center;
}

.history-loading,
.history-empty {
  color: #888;
  text-align: center;
  padding: 40px 0;
}

.history-list {
  max-height: 700px;
  overflow-y: auto;
}

.history-entry {
  background-color: #1a1a1a;
  border-radius: 5px;
  padding: 12px 15px;
  margin-bottom: 10px;
  border-left: 3px solid #4CAF50;
}

.history-entry.history-failed {
  border-left-color: #ff5555;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.history-badge {
  font-size: 11px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 3px;
}

.badge-success {
  background-color: #4CAF50;
  color: white;
}

.badge-error {
  background-color: #ff5555;
  color: white;
}

.history-type-badge {
  font-size: 11px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 3px;
  background-color: #5A3E85;
  color: white;
}

.history-time {
  color: #888;
  font-size: 12px;
  margin-left: auto;
}

.history-title {
  color: white;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 4px;
}

.history-body {
  color: #ccc;
  font-size: 13px;
  margin-bottom: 6px;
}

.history-meta {
  color: #888;
  font-size: 12px;
}

.history-error {
  color: #ff5555;
  font-size: 12px;
  margin-top: 6px;
  padding: 6px 8px;
  background-color: rgba(255, 85, 85, 0.1);
  border-radius: 3px;
}

@media (max-width: 900px) {
  .two-column-layout {
    flex-direction: column;
  }

  .left-column,
  .right-column {
    flex: 1 1 auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Admin-Push-Notifications.vue